/**
 * AnnouncementsPopup Component
 *
 * Modal that automatically appears when there are unread announcements.
 * Shows all unread announcements from the last 3 days in a single modal.
 * Dismissing the modal marks all as read.
 *
 * Usage:
 *   // In AuthenticatedLayout
 *   <AnnouncementsPopup
 *     announcements={popupAnnouncements}
 *     onDismiss={dismissAll}
 *   />
 */

import React from 'react';
import { BaseModal } from './BaseModal';
import { sanitizeRichHtml } from '../utils/sanitizeHtml';
import type { Announcement } from '../hooks/useAnnouncements';

export interface AnnouncementsPopupProps {
  /** Announcements to display in the popup */
  announcements: Announcement[];
  /** Called when user dismisses the modal (marks all as read) */
  onDismiss: () => void;
}

/**
 * Format date for display
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) {
    return 'Just now';
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else {
    return `${diffDays} days ago`;
  }
}

export const AnnouncementsPopup: React.FC<AnnouncementsPopupProps> = ({
  announcements,
  onDismiss,
}) => {
  // Don't render if no announcements
  if (announcements.length === 0) {
    return null;
  }

  return (
    <BaseModal
      isOpen={true}
      onClose={onDismiss}
      title={`Announcement${announcements.length > 1 ? 's' : ''}`}
      icon="fa-solid fa-bullhorn text-hue-blue-text"
      maxWidth="lg"
      closeOnBackdrop={true}
      closeOnEscape={true}
      footer={
        <div className="flex justify-end">
          <button
            onClick={onDismiss}
            className="px-6 py-2 bg-action text-ink-inverse rounded-lg hover:bg-action-hover transition-colors font-medium"
          >
            Got It
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {announcements.map((announcement, index) => (
          <div key={announcement.id}>
            {/* Announcement Card */}
            <div className="bg-surface-muted rounded-lg p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-ink text-lg">
                  {announcement.title}
                </h3>
                <span className="text-xs text-ink-muted whitespace-nowrap ml-4">
                  {formatRelativeTime(announcement.createdAt)}
                </span>
              </div>

              {/* Message */}
              {announcement.type === 'rich' && announcement.htmlContent ? (
                <div
                  className="prose prose-sm prose-slate max-w-none
                    prose-headings:text-ink prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
                    prose-p:text-ink-body prose-p:my-2
                    prose-a:text-hue-cyan-text prose-a:hover:text-hue-cyan-deep
                    prose-ul:my-2 prose-li:my-0
                    prose-strong:text-ink-body
                    user-authored"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(announcement.htmlContent) }}
                />
              ) : (
                <p className="text-ink-body whitespace-pre-wrap">
                  {announcement.message}
                </p>
              )}

              {/* Date */}
              <div className="mt-3 text-xs text-ink-faint">
                {formatDate(announcement.createdAt)}
              </div>
            </div>

            {/* Divider between announcements (except last) */}
            {index < announcements.length - 1 && (
              <div className="border-b border-line my-4" />
            )}
          </div>
        ))}
      </div>
    </BaseModal>
  );
};

export default AnnouncementsPopup;
